import React from 'react';
import { Card, Space, Typography, Tag, Empty, Row, Col, Badge } from 'antd';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ClockCircleOutlined,
  SyncOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
  ApiOutlined,
  FireOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { MethodBadge } from '../Common';

const { Title, Text } = Typography;

// Arrow connector components with pure CSS
const RightArrowConnector: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    margin: '0 12px',
    flexShrink: 0
  }}>
    <div style={{
      width: '40px',
      height: '3px',
      background: 'linear-gradient(90deg, #1890ff 0%, #40a9ff 100%)',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        right: '-8px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderLeft: '10px solid #40a9ff'
      }} />
    </div>
  </div>
);

const WrapAroundConnector: React.FC = () => (
  <div style={{
    width: '100%',
    height: '60px',
    position: 'relative'
  }}>
    {/* Vertical line from right going down */}
    <div style={{
      position: 'absolute',
      right: 'calc((100% - 120px) / 6 + 8px)',
      top: '0',
      width: '3px',
      height: '30px',
      background: '#1890ff'
    }} />

    {/* Horizontal line going across */}
    <div style={{
      position: 'absolute',
      left: 'calc((100% - 120px) / 6 + 8px)',
      right: 'calc((100% - 120px) / 6 + 8px)',
      top: '30px',
      height: '3px',
      background: '#1890ff'
    }} />

    {/* Vertical line from left going down */}
    <div style={{
      position: 'absolute',
      left: 'calc((100% - 120px) / 6 + 8px)',
      top: '30px',
      width: '3px',
      height: '30px',
      background: '#1890ff'
    }} />

    {/* Arrow pointing down into next row */}
    <div style={{
      position: 'absolute',
      left: 'calc((100% - 120px) / 6 + 9.5px)',
      top: '60px',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '10px solid #40a9ff'
    }} />
  </div>
);

interface PipelineOverviewViewerProps {
  dataPipeline: any;
}

export const PipelineOverviewViewer: React.FC<PipelineOverviewViewerProps> = ({ dataPipeline }) => {
  console.log('[PipelineOverviewViewer] Received dataPipeline:', dataPipeline);

  if (!dataPipeline) {
    return (
      <Empty
        description="No pipeline data available"
        style={{ padding: '40px 0' }}
      />
    );
  }

  // Extract stage statuses
  const configStatus = dataPipeline?.ai_processor_response_config?.state;
  const verifyStatus = dataPipeline?.ai_processor_response_verify?.state;
  const executeStatus = dataPipeline?.ai_processor_response_execution?.state;

  console.log('[PipelineOverviewViewer] Statuses:', { configStatus, verifyStatus, executeStatus });

  // Determine overall pipeline status
  const getOverallStatus = () => {
    if (configStatus === 'FAILED' || verifyStatus === 'FAILED' || executeStatus === 'FAILED') {
      return { status: 'error', text: 'FAILED', icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" /> };
    }
    if (configStatus === 'WAITING_FOR_RESPONSE' || verifyStatus === 'WAITING_FOR_RESPONSE' || executeStatus === 'WAITING_FOR_RESPONSE') {
      return { status: 'processing', text: 'IN PROGRESS', icon: <SyncOutlined spin style={{ color: '#1890ff' }} /> };
    }
    if (configStatus === 'OK' && verifyStatus === 'OK' && executeStatus === 'OK') {
      return { status: 'success', text: 'SUCCESS', icon: <CheckCircleTwoTone twoToneColor="#52c41a" /> };
    }
    // Handle partial completion - if at least config is OK
    if (configStatus === 'OK') {
      return { status: 'processing', text: 'IN PROGRESS', icon: <SyncOutlined spin style={{ color: '#1890ff' }} /> };
    }
    return { status: 'default', text: 'PENDING', icon: <ClockCircleOutlined /> };
  };

  const overallStatus = getOverallStatus();

  // Get stage data - need to access .data property
  const configData = dataPipeline?.config?.data;
  const executeData = dataPipeline?.execution?.data;

  console.log('[PipelineOverviewViewer] Config data:', configData);
  console.log('[PipelineOverviewViewer] Execute data:', executeData);

  const description = configData?.description || 'No description available';
  const stepsCount = configData?.config_steps?.length || 0;
  const configSteps = configData?.config_steps || [];

  // Create a map of execution results by step name for matching
  const executionResultsMap = new Map();
  if (executeData?.steps) {
    executeData.steps.forEach((result: any) => {
      executionResultsMap.set(result.name, result);
    });
  }

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high':
        return { border: '#ff4d4f', bg: '#fff2f0' };
      case 'medium':
        return { border: '#faad14', bg: '#fffbe6' };
      case 'low':
        return { border: '#52c41a', bg: '#f6ffed' };
      default:
        return { border: '#d9d9d9', bg: '#fafafa' };
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Description Card */}
      <Card
        title={<span style={{ fontSize: '16px', fontWeight: 600 }}>📋 Pipeline Description</span>}
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #f0f0f0'
        }}
        headStyle={{
          background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text style={{ fontSize: '13px', lineHeight: '1.6' }}>
            {description}
          </Text>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Total Steps: <Text strong>{stepsCount}</Text>
          </Text>
        </Space>
      </Card>

      {/* Visual Flow of Steps - Clean Grid */}
      <Card
        title={<span style={{ fontSize: '16px', fontWeight: 600 }}>🔄 Configuration Flow</span>}
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #f0f0f0'
        }}
        headStyle={{
          background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {Array.from({ length: Math.ceil(configSteps.length / 3) }, (_, rowIndex) => {
            const rowStart = rowIndex * 3;
            const rowEnd = Math.min(rowStart + 3, configSteps.length);
            const rowSteps = configSteps.slice(rowStart, rowEnd);
            const isLastRow = rowEnd === configSteps.length;

            return (
              <React.Fragment key={rowIndex}>
                {/* Row of cards */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  {rowSteps.map((step: any, indexInRow: number) => {
                    const globalIndex = rowStart + indexInRow;
                    const riskColors = getRiskColor(step.explanation?.risk_level);
                    const executionResult = executionResultsMap.get(step.name);
                    const executionStatus = executionResult?.status;
                    const isLastInRow = indexInRow === rowSteps.length - 1;

                    return (
                      <React.Fragment key={globalIndex}>
                        <div
                          style={{
                            width: 'calc((100% - 120px) / 3)',
                            minWidth: '260px',
                            height: '140px',
                            padding: '16px',
                            borderRadius: '10px',
                            border: `2px solid ${riskColors.border}`,
                            background: riskColors.bg,
                            transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
                            cursor: 'default',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Badge
                              count={globalIndex + 1}
                              style={{
                                backgroundColor: '#1890ff',
                                fontWeight: 600,
                                fontSize: '13px',
                                height: '24px',
                                lineHeight: '24px',
                                minWidth: '24px'
                              }}
                            />
                            <Space size={8}>
                              {step.explanation?.risk_level && (
                                <div style={{ fontSize: '18px' }}>
                                  {step.explanation.risk_level === 'high' ? <FireOutlined style={{ color: '#ff4d4f' }} /> :
                                   step.explanation.risk_level === 'medium' ? <ExclamationCircleOutlined style={{ color: '#faad14' }} /> :
                                   <SafetyOutlined style={{ color: '#52c41a' }} />}
                                </div>
                              )}
                              {executionStatus && (
                                <div style={{ fontSize: '18px' }}>
                                  {executionStatus === 'success' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                                   executionStatus === 'failed' ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> :
                                   <WarningOutlined style={{ color: '#faad14' }} />}
                                </div>
                              )}
                            </Space>
                          </div>
                          <Text
                            strong
                            style={{
                              fontSize: '14px',
                              lineHeight: '1.4',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={step.name}
                          >
                            {step.name}
                          </Text>
                          <div>
                            <MethodBadge method={step.method} />
                          </div>
                        </div>
                        {/* Right arrow between cards in same row */}
                        {!isLastInRow && <RightArrowConnector />}
                      </React.Fragment>
                    );
                  })}
                </div>
                {/* Wrap-around connector between rows */}
                {!isLastRow && <WrapAroundConnector />}
              </React.Fragment>
            );
          })}
        </Space>
      </Card>
    </Space>
  );
};

export default PipelineOverviewViewer;
